import { takeEvery, takeLatest, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  getExtStatus, handleSaveSaleData, paymentSuccessPopup, updateCancelPayment, updatePaymentClose,
  updatePaymentSuccess, getExtLinkForBalance, updatePaymentToServer, updatePaymnetFailure,
  updatePrepaidPayment, updateSalePayment, updateUpiPayment, updateUpiPaymentSuccess,
  isReturnSale, saleReturnClose, checkForProviderBalance, checkForOtp, refundPayment,
  fetchRistaCards, setRistaCardList, validateRistaCard,
} from "../../slices/CheckoutSlice";
import { getLocalStorage } from "../../../utils/localStorage";
import { CLOSE_NEGATIVE_BALANCE_CODE, deleteErrorsField, preventCloseSale } from "../../../helpers/commonHelpers";
import { updateSyncedSale } from "../../../helpers/OfflineHelper";
import { fetchDeviceConfig, fetchDeviceConfigApiResponse } from "../../slices/LocalPaymentInfo";
import { removeAdhocTable, updateAdhocTables } from "../../../helpers/adhocTableHelpers";
import { SALE_STATUS_TYPES } from "../../../utils/constants";
import { saveBackOrderShowModal } from "../../slices/CustomerSlice";
import { REST_SALE_BACK_ITEMS_NOT_PROCESSED, INSUFFICIENT_STOCK } from "../../../data/constants";
import { hasItemLeveErrors, summarizeSaleData } from "../../../helpers/saleHelpers";
import { GA_CATEGORIES, GA_EVENTS } from "../../../data/gaConstants";
import { fireOrderPlacedEvent } from "../../../helpers/cartAuditHelper";
import { notifySuccess, printSaleTickets, sendGTMEvent } from "../../../effects-bridge";

export * from "./shared";

/**
 * WEB variant of `redux-store/saga/checkout`. See `./shared.ts` for the split.
 *
 * ⚠️ **Three capabilities come from the EFFECTS BRIDGE** rather than from app helpers:
 * `printSaleTickets` (behind ~1100 lines of printer glue), `sendGTMEvent` (203 lines on
 * react-ga4) and `notifySuccess` (the toast). Every `ToastNotifySuccess(` call site became
 * `notifySuccess(` — the same function this app wires at boot, so behaviour is unchanged.
 *
 * ⚠️ **`saveSaleLocally` was DROPPED from the imports** (rule 2b / hazard 13): the only
 * occurrence in this file was a commented-out call. Session 26 recorded it as a phantom in all
 * three files that imported it; this is one of them.
 */

async function commonPostData(data: any): Promise<any> {
  try {
    const postApi = api.request(data);
    const fetchResponse = await postApi;
    return fetchResponse;

  } catch (e) {
    console.log("error ", e);
  }
}

function* updatePaymentInfo(actions: any): any {
  let url = ENDPOINT.PAYMENT.updatePayment();
  try {
    let data = actions.payload;
    let saleData = data.postBody;
    saleData = deleteErrorsField(saleData);
    if (preventCloseSale(saleData, {url: url, functionName: "updatePaymentInfo"})) {
      // answer the caller: the payment engine waits on this callback, and returning without it
      // left the checkout stuck mid-commit. v1's callers toast the code's words — the guard's own
      // (docs/flows/PAYMENT_ENGINE.md §37)
      data.callback && data.callback({ code: CLOSE_NEGATIVE_BALANCE_CODE });
      return;
    }
    let response = yield commonPostData({
      url,
      data: JSON.stringify(saleData),
      method: "POST",
      headers: { "Content-Type": "application/json" },
      hideLoader: false
    });
    if (
      response &&
      !!Object.keys(response)?.length &&
      !response?.errors?.length &&
      !response?.warnings?.length &&
      !response?.code &&
      !hasItemLeveErrors(response?.saleItems) &&
      response?.saleStatus === SALE_STATUS_TYPES.CLOSED
    ) {
      const saleSummary = summarizeSaleData(response);
      sendGTMEvent({
        eventName: GA_EVENTS.ORDER_COMPLETED,
        params: {
          category: GA_CATEGORIES.SALE,
          ...saleSummary,
          saleId: response?.mid
        }
      });

      // Fire ORDER_PLACED cart audit event
      fireOrderPlacedEvent(response?.mid, response?.billTotal || 0);

      yield put(paymentSuccessPopup(response));
    }
    if (response) {
      if(response?.code === REST_SALE_BACK_ITEMS_NOT_PROCESSED || response?.errors?.includes(INSUFFICIENT_STOCK)) {
         yield put(saveBackOrderShowModal(true))
      }
      if (!response.operation) {
        updateSyncedSale(response);
      }
      yield put(updatePaymentSuccess(response));
      if (!response.code) {
        removeAdhocTable(response);
      }
      if (data.callback) {
        data.callback(response);
      }
    } else {
      yield put(updatePaymnetFailure(response));
      if (data.callback) {
        data.callback(response);
      }
    }
  }
  catch (e) {
    yield put((updatePaymnetFailure as any)({ code: "UI_GENERIC_MESSAGE", status: false }));
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }

}

function* updatePaymentCloseData(actions: any): any {
  // debugger
  try {
    let data = actions.payload;
    let url = ENDPOINT.PAYMENT.paymentClose(data.mid);
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: "POST",
      headers: { "Content-Type": "application/json" },
      hideLoader: false
    });
    if (response) {
      if (!response.operation) {
        updateSyncedSale(response);
      }
      yield put(updatePaymentSuccess(response));
      printSaleTickets(response, response?.tickets)
      if (data.callback) {
        data.callback(response);
      }
    } else {
      yield put(updatePaymnetFailure(response));
      if (data.callback) {
        data.callback(response);
      }
    }
  }
  catch (e) {
    yield put((updatePaymnetFailure as any)({ code: "UI_GENERIC_MESSAGE", status: false }));
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }

}

function* updateSaleData(actions: any): any {
  let url = ENDPOINT.PAYMENT.saveSaleApi();
  let dataToSend = actions.payload?.postBody;
  let isOrderClosed = dataToSend?.saleStatus === SALE_STATUS_TYPES.CLOSED;
  if (isOrderClosed) {
    url = ENDPOINT.PAYMENT.updateClosedSalePayment(dataToSend.mid);
    dataToSend = { ...dataToSend.paymentInfo };
  }
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url,
      data: JSON.stringify(dataToSend),
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      hideLoader: false
    });
    console.log("response-----: ", response);
    if (response) {
      if (!response?.code && !response.operation) {
        updateSyncedSale(response);
      }
      updateAdhocTables(response);
      yield put(updatePaymentSuccess(response));
      //saveSaleLocally(response);

      // Fire ORDER_PLACED cart audit event for successful save
      if (!response?.code) {
        fireOrderPlacedEvent(response?.mid, response?.billTotal || 0);
      }

      if (data.callback) {
        data.callback(response);
      }
    } else {
      yield put(updatePaymnetFailure(response));
      if (data.callback) {
        data.callback(response);
      }
    }
  }
  catch (e) {
    yield put((updatePaymnetFailure as any)({ code: "UI_GENERIC_MESSAGE", status: false }));
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* updateUpiData(actions: any): any {
  let url = ENDPOINT.PAYMENT.saveSaleApi();
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
      headers: { "Content-Type": "application/json" }
    });
    console.log("response-----: ", response);
    if (response) {
      updateAdhocTables(response);
      yield put(updateUpiPaymentSuccess(response));
      if (data.callback) {
        data.callback(response);
      }
    } else {
      yield put(updateUpiPaymentSuccess(response));
      if (data.callback) {
        data.callback(response);
      }
    }
  }
  catch (e) {
    yield put((updatePaymnetFailure as any)({ code: "UI_GENERIC_MESSAGE", status: false }));
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* updatePrepaidPaymentData(actions: any): any {
  let url = ENDPOINT.PAYMENT.prepaidPaymentApi();
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
      headers: { "Content-Type": "application/json" }
    });
    console.log("response-----: ", response);
    yield put(updateUpiPaymentSuccess(response));
    if (data.callback) {
      data.callback(response);
    }
  }
  catch (e) {
    yield put((updatePaymnetFailure as any)({ code: "UI_GENERIC_MESSAGE", status: false }));
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* getConfigData(actions: any): any {
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url: data.url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });
    console.log("response-----: ", response);
    yield put((fetchDeviceConfigApiResponse as any)());
    if (data.callback) {
      data.callback(response);
    }
  }
  catch (e) {
    console.log("error ", e);
    yield put((fetchDeviceConfigApiResponse as any)());
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* updatePayment(actions: any): any {
  let url = ENDPOINT.PAYMENT.salePaymentSave();
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });
    console.log("response-----: ", response);
    if (data.callback) {
      data.callback(response);
    }
  }
  catch (e) {
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

/**
 * ⚠️ `skipGlobalLoader` rides on the ACTION, not on `postBody` — it is an axios-config key
 * (`utils/apiService`'s request interceptor), so it has to sit beside `url`/`method`, and a
 * flag inside `postBody` would just be posted to the server.
 *
 * Additive on purpose: v1's `CheckoutContainer` dispatches `getExtStatus` without it, so its
 * poll keeps the loader it has always put up. The caller that sets it is the payment engine's
 * transport, whose runner re-polls every 10s — a full-screen loader blinking on a timer over a
 * QR the customer is trying to scan.
 */
function* fetchExtStatus(actions: any): any {
  let url = ENDPOINT.PAYMENT.extPaymentStatus();
  try {
    let data = actions.payload;
    console.log("payload-----: ", data);
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
      ...(data.skipGlobalLoader ? { skipGlobalLoader: true } : {}),
    });
    console.log("response-----: ", response);
    if (data.callback) {
      data.callback(response);
    }
  }
  catch (e) {
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* updatingExtStatus(actions: any): any {
  let url = ENDPOINT.PAYMENT.extPaymentCancel();
  try {
    let data = actions.payload;
    console.log("payload-----: ", data);
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });
    console.log("response-----: ", response);
    if (data.callback) {
      data.callback(response);
    }
  }
  catch (e) {
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* refundExtPayment(actions: any): any {
  let url = ENDPOINT.PAYMENT.extPaymentRefund;
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });
    if (data.callback) {
      data.callback(response);
    }
  }
  catch (e) {
    console.log("error ", e);
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* closeSaleReturn(actions: any): any {
  try {
    let data = actions.payload;
    let response = yield commonPostData({
      url: 'sale/return/close',
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });

    if(!response?.code)
    {
      notifySuccess('Sale return order has been created')
    }
    // the RESPONSE, as every other checkout saga passes it: the payment engine reads success or
    // failure from it (docs/flows/PAYMENT_ENGINE.md §36 G1). v1's callback takes no argument.
    data.callback && data.callback(response);
    yield put(isReturnSale(false))
  }
  catch (e) {
    console.log("error ", e);
    yield put(isReturnSale(false))
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* fetchProviderBalance(actions: any): any {
  try {
    let data = actions.payload.dataToSend;
    let providerId = actions.payload.providerId;
    const url = ENDPOINT.PAYMENT.extPaymentBalance(providerId)
    let response = yield commonPostData({
      url: url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });

    if(!response?.code)
    {
      notifySuccess(response?.code)
    }
    data.callback && data.callback(response);
  }
  catch (e) {
    console.log("error ", e);
    yield put(isReturnSale(false))
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}


function* fetchOtp(actions: any): any {
  try {
    let data = actions.payload.dataToSend;
    let providerId = actions.payload.providerId;
    const url = ENDPOINT.PAYMENT.extPaymentOtp(providerId)
    let response = yield commonPostData({
      url: url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });

    if(!response?.code)
    {
      notifySuccess(response?.code)
    }
    data.callback && data.callback(response);
  }
  catch (e) {
    console.log("error ", e);
    yield put(isReturnSale(false))
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* getExtBalanceLink(actions: any): any {
  try {
    let data = actions.payload.dataToSend;
    let providerId = actions.payload.providerId;
    const url = ENDPOINT.PAYMENT.extAddBalanceLink(providerId)
    let response = yield commonPostData({
      url: url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });

    if(!response?.code)
    {
      notifySuccess(response?.code)
    }
    data.callback && data.callback(response);
  }
  catch (e) {
    console.log("error ", e);
    yield put(isReturnSale(false))
    if (actions.payload.callback) {
      actions.payload.callback({ code: "UI_GENERIC_MESSAGE", status: false });
    }
  }
}

function* fetchRistaCardsData(actions: any): any {
  const url = ENDPOINT.RISTACARD.fetch();
  try {
    const data = actions.payload;
    const response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });
    
    if (response && response.results) {
      yield put(setRistaCardList(response.results));
    } else {
      yield put(setRistaCardList([]));
    }
  } catch (e) {
    yield put(setRistaCardList([]));
  }
}

function* validateRistaCardData(actions: any): any {
  const url = ENDPOINT.RISTACARD.validate();
  try {
    const data = actions.payload;
    const response = yield commonPostData({
      url,
      data: JSON.stringify(data.postBody),
      method: 'POST',
    });
    if (data.callback) data.callback(response);
  } catch (e) {
    if (actions.payload.callback) actions.payload.callback({ status: false });
  }
}

export default function* root(): any {
  yield takeEvery(updatePaymentToServer.type, updatePaymentInfo);
  yield takeEvery(updatePaymentClose.type, updatePaymentCloseData);
  yield takeEvery(handleSaveSaleData.type, updateSaleData);
  yield takeEvery(updateUpiPayment.type, updateUpiData);
  yield takeEvery(updatePrepaidPayment.type, updatePrepaidPaymentData);
  yield takeEvery(fetchDeviceConfig.type, getConfigData);
  yield takeEvery(updateSalePayment.type, updatePayment);
  yield takeLatest(getExtStatus.type, fetchExtStatus);
  yield takeEvery(updateCancelPayment.type, updatingExtStatus);
  yield takeEvery(saleReturnClose.type, closeSaleReturn);
  yield takeEvery(checkForProviderBalance, fetchProviderBalance);
  yield takeEvery(checkForOtp.type, fetchOtp);
  yield takeEvery(getExtLinkForBalance.type, getExtBalanceLink)
  yield takeEvery(refundPayment.type, refundExtPayment);
  // yield takeEvery(updateExternalPayment.type, updatePayment);
  yield takeEvery(fetchRistaCards.type, fetchRistaCardsData);
  yield takeEvery(validateRistaCard.type, validateRistaCardData);

}
